import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { ConditionalWrapper } from 'components/conditional-wrapper';
import FullPageScreen from 'components/full-page-screen';
import LoadingAnimation from 'components/loading-animation';
import PaymentMethod from 'components/payment-method';
import {
  CartPaymentCardType,
  IGenerateVrPaymentVaultLinkMutation,
  useGenerateVrPaymentVaultLinkMutation,
} from 'generated/rbi-graphql';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { usePaymentContext } from 'state/payment';
import {
  CASH_ACCOUNT_IDENTIFIER,
  PAYPAL_PAYMENT_METHOD_PLACEHOLDER,
} from 'state/payment/constants';
import { CardTypes } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import { IVrPaymentState } from 'utils/payment';
import { routes } from 'utils/routing';

import { PlaceOrderButton } from '../../../place-order-button';
import SectionHeading from '../../../section-heading';
import { AddNewCreditCardWrapper } from '../../add-new-credit-card-wrapper';
import DeliveryCancellationPolicy from '../../delivery-cancellation-policy';
import PaymentSelection from '../../payment-selection';
import { PaymentInfoBackground, PaymentSection } from '../../styled';
import { OrderPaymentProps } from '../../types';
import { useVrPaymentPayPal } from '../../use-vrpayment-paypal';
import Totals from '../Totals';
import { useOrderPayment } from '../use-order-payment';

import { HostedPaymentForm } from './hosted-payment-form/hosted-payment-form';
import { HostedPaymentVaultedCardForm } from './hosted-payment-vaulted-card-form/hosted-payment-vaulted-card-form';
import { IVrPaymentVaultLinkResult, OutcomeResult } from './types';

const StyledFullPageScreen = styled(FullPageScreen)`
  background: ${Styles.color.background};

  h1 {
    text-transform: capitalize;
  }
`;

export const HostedPagePayment: React.FC<OrderPaymentProps> = ({
  serverOrder,
  cartEntries,
  onPriceOrder,
  pricedAndAvailable,
}) => {
  const { formatMessage } = useIntl();
  const [vaultedPaymentData, setVaultedPaymentData] = useState<IVrPaymentVaultLinkResult | null>(
    null
  );

  const { serviceMode, isDelivery, isCurbside, isTableService } = useServiceModeContext();
  const order = useOrderContext();
  const { checkoutPaymentMethodId } = usePaymentContext();

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [isPaymentError, setIsPaymentError] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const { enableOrderTimedFire } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });
  const {
    enableDelivery,
    ErrorDialog,
    handlePaymentMethodSelected,
    handleVrPaymentSuccess,
    isCashPayment,
    isPlacingOrder,
    isValidTip,
    offerIsValid,
    paymentMethods,
    placeOrderAriaLabel,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    setShowAddPaymentMethod,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showPaymentMethodSelection,
  } = useOrderPayment({
    enableOrderTimedFire,
    onPriceOrder,
    serverOrder,
  });
  const paypal = useVrPaymentPayPal();
  const { getErrorMessages } = useGetPaymentErrors();

  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError: isPaymentError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const handleVrPaymentVaultLinkCompleted = (result: IGenerateVrPaymentVaultLinkMutation) => {
    setIsProcessingPayment(false);
    setVaultedPaymentData(result.generateVrPaymentVaultLink);
  };

  const [generateVrPaymentVaultLink] = useGenerateVrPaymentVaultLinkMutation({
    onCompleted: handleVrPaymentVaultLinkCompleted,
    onError: error => {
      logger.error({
        message: 'VR Payment: Vaulted CC integration threw an error',
        error,
      });

      throw error;
    },
  });

  const onPaymentOutcome = useCallback(
    (outcome: OutcomeResult) => {
      setIsProcessingPayment(false);
      if (vaultedPaymentData) {
        setVaultedPaymentData(null);
      }

      if (outcome.type === 'Failed') {
        logger.error({
          message: 'VR Payment: Hosted page integration failed',
          outcome,
        });

        setIsPaymentError(true);
        setIsCheckoutErrorsModalOpen(true);
        if (showAddPaymentMethod) {
          // if the "Add New Payment Method" section is expanded,
          // let's close it and re-open it a bit later to
          // reload the hosted payment form
          setShowAddPaymentMethod(false);
          setTimeout(() => setShowAddPaymentMethod(true), 1000);
        }
        return;
      }
      const { merchantAccount, paymentMethod, pspReference } = outcome;

      if (paymentMethod.type === 'paypalfast') {
        handleVrPaymentSuccess({
          merchantAccount,
          pspReference,
          nameOnCard: paymentMethod.email,
          cardNumber: '',
          isCardNumberValid: true,
          isExpiryValid: true,
          paypal: {
            billingAgreementId: paymentMethod.billingAgreementId,
          },
          cardType: CartPaymentCardType.PAYPAL,
          cvv: '',
          expiry: '',
          saveCard: false,
          billingStreetAddress: null,
          billingApt: null,
          billingCity: null,
          billingState: null,
          billingZip: '',
          billingCountry: null,
          billingAddressSameAsDelivery: false,
        });
      } else {
        handleVrPaymentSuccess({
          merchantAccount,
          pspReference,
          nameOnCard: paymentMethod.cardholderName,
          cardNumber: paymentMethod.storePaymentMethod ? paymentMethod.storedPaymentMethodId : '',
          isCardNumberValid: true,
          isExpiryValid: true,
          card: {
            bin: paymentMethod.bin,
            last4: paymentMethod.last4,
            expiryMonth: paymentMethod.expiryMonth,
            expiryYear: paymentMethod.expiryYear,
          },
          cardType: paymentMethod.brand,
          cvv: '',
          expiry: `${paymentMethod.expiryMonth}${paymentMethod.expiryYear}`,
          saveCard: paymentMethod.storePaymentMethod,
          billingStreetAddress: null,
          billingApt: null,
          billingCity: null,
          billingState: null,
          billingZip: '',
          billingCountry: null,
          billingAddressSameAsDelivery: false,
        });
      }

      setIsHandlingOrderCommit(true);
    },
    [
      handleVrPaymentSuccess,
      setShowAddPaymentMethod,
      showAddPaymentMethod,
      vaultedPaymentData,
      setIsHandlingOrderCommit,
    ]
  );

  if (!serverOrder) {
    return null;
  }

  // Handles generating and auto-submitting a vaulted payment form if the user selects to pay with a saved credit card
  const handleVaultedVrPayment = async () => {
    if (!selectedMethod || !selectedMethod.accountIdentifier) {
      return;
    }

    // If any error is present at last step we abort payment and show error modal
    if (paymentErrorMessages?.length) {
      setIsCheckoutErrorsModalOpen(true);
      return;
    }

    setIsProcessingPayment(true);

    if (selectedMethod.accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
      shouldConfirmStoreOnCheckout(true);
      if (isCurbside && onPriceOrder) {
        setCommitOrderAfterPricing(true);
        return onPriceOrder({
          customerName: `CS-${LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION)}`,
        });
      }
      if (isTableService && onPriceOrder) {
        setCommitOrderAfterPricing(true);
        return onPriceOrder({
          customerName: `TABLE-${LocalStorage.getItem(StorageKeys.TABLE_NUMBER)}`,
        });
      }
      setIsHandlingOrderCommit(true);
      setIsProcessingPayment(false);
      return;
    }

    if (
      selectedMethod.accountIdentifier === CardTypes.APPLE_PAY ||
      selectedMethod.accountIdentifier === CardTypes.GOOGLE_PAY
    ) {
      setIsHandlingOrderCommit(true);
      return;
    }

    if (selectedMethod.accountIdentifier === PAYPAL_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier) {
      paypal.openModal(serverOrder.rbiOrderId).then(onPaymentOutcome);
      return;
    }

    if (selectedMethod.paypal) {
      const deviceToken = await paypal.getDeviceToken();
      handleVrPaymentSuccess({
        paypal: {
          deviceToken,
        },
      } as IVrPaymentState);
      setIsHandlingOrderCommit(true);
      return;
    }

    const { colorDepth, width, height } = window.screen;
    const now = new Date();
    const timeZoneOffset = now.getTimezoneOffset();

    await generateVrPaymentVaultLink({
      variables: {
        input: {
          rbiOrderId: serverOrder.rbiOrderId,
          accountIdentifier: selectedMethod.accountIdentifier,
          browserInfo: {
            colorDepth,
            screenWidth: width,
            screenHeight: height,
            timeZoneOffset,
          },
        },
      },
    });
  };

  const showPlaceOrderButton =
    (selectedMethod?.credit && !vaultedPaymentData) ||
    selectedMethod?.paypal ||
    selectedMethod?.cash;

  return (
    <StyledFullPageScreen heading={formatMessage({ id: 'orderPayment' })} closeHref={routes.cart}>
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      <PaymentSection data-testid="cart-hosted-page-payment">
        <SectionHeading id="payment-method-header">
          {formatMessage({ id: 'paymentDetails' })}
        </SectionHeading>
        <PaymentSelection>
          <Totals
            serverOrder={serverOrder}
            order={order}
            serviceMode={serviceMode}
            isDisabled={isProcessingPayment || isPlacingOrder}
          />
          {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
          <PaymentInfoBackground $hostedPayment>
            {showPaymentMethodSelection && (
              <PaymentMethod
                aria-expanded="true"
                aria-labelledby="payment-method-header"
                hasBorder={false}
                isAddCardOpen={showAddPaymentMethod}
                methods={paymentMethods}
                openAddCardOnClick={setShowAddPaymentMethod}
                handleSelect={handlePaymentMethodSelected}
                selectedMethodId={checkoutPaymentMethodId}
              />
            )}
            {vaultedPaymentData && (
              <HostedPaymentVaultedCardForm onResult={onPaymentOutcome} data={vaultedPaymentData} />
            )}
            {showAddPaymentMethod && (
              <ConditionalWrapper
                condition={!paymentMethods.length}
                wrapper={children => <AddNewCreditCardWrapper>{children}</AddNewCreditCardWrapper>}
              >
                <HostedPaymentForm
                  rbiOrderId={serverOrder.rbiOrderId ?? ''}
                  onResult={onPaymentOutcome}
                />
              </ConditionalWrapper>
            )}
            {isPlacingOrder && !isCashPayment && (
              <LoadingAnimation fillColor={Styles.color.primary} />
            )}
          </PaymentInfoBackground>
          {showPlaceOrderButton && (
            <PlaceOrderButton
              confirmation={false}
              disabled={isProcessingPayment || isPlacingOrder}
              isLoading={isProcessingPayment || isPlacingOrder}
              margin={selectedMethod ? '1rem 0 0' : '0.5rem 0 0'}
              onClick={handleVaultedVrPayment}
              placeOrderAriaLabel={placeOrderAriaLabel}
            />
          )}
        </PaymentSelection>
        <ErrorDialog />
      </PaymentSection>
    </StyledFullPageScreen>
  );
};

export default HostedPagePayment;
